/**
 * Raise Package form bind: lead search, live math, and three-file generate.
 */
import { authenticatedFetch, DashboardAuthRequiredError } from "./dashboard-auth.js";
import { escapeAttr, escapeHtml } from "./dashboard-html.js";
import { fetchJson } from "./dashboard-lead-factory-fetch.js";
import type {
  LeadFactoryFact,
  LeadFactoryLead,
  LeadFactoryLeadDetailResponse,
  LeadFactoryLeadsResponse
} from "./dashboard-lead-factory-types.js";
import {
  raisePackageMemorandumTemplateUrl,
  raisePackageNoteTemplateUrl,
  raisePackageWireTemplateUrl
} from "./dashboard-urls.js";
import { copyWireDocument, fillDocxTemplate } from "./raise-package-docx.js";
import {
  computeRaisePackageFields,
  DEFAULT_INTEREST_RATE,
  DEFAULT_TERM_MONTHS,
  isWholeDollarPrincipal,
  parseToCents,
  raisePackageFilenames,
  type RaisePackageInput
} from "./raise-package-fields.js";

const SEARCH_DEBOUNCE_MS = 250;
const DOWNLOAD_STAGGER_MS = 150;
const ASHLEY_SAMPLE_ID = "raise_sample_100_ashley";

/** Drive-folder sample. Not a Lead Factory row — search used to miss it. */
const ASHLEY_SAMPLE_LEAD: LeadFactoryLead = {
  id: ASHLEY_SAMPLE_ID,
  county: "Harnett",
  status: "raise_sample",
  identity_state: "property_address_known",
  contact_readiness: "ready",
  property_address: "100 Ashley Street",
  owner_name: "Valerie Andrew",
  parcel_id: null,
  priority: "unscored",
  created_at: "2026-08-21T00:00:00.000Z",
  updated_at: "2026-08-21T00:00:00.000Z"
};

const ASHLEY_SAMPLE_FACTS: LeadFactoryFact[] = [
  fact("estimated_excess_proceeds", "$24,264.23"),
  fact("heir_count", "4"),
  fact("ownership_interest", "25%")
];

function fact(field_name: string, value: string): LeadFactoryFact {
  return {
    id: `raise_sample_fact_${field_name}`,
    source_document_id: "raise_sample_drive_docs",
    field_name,
    value,
    normalized_value: value,
    confidence: 1,
    provenance: "drive_raise_docs",
    review_status: "accepted",
    created_at: "2026-08-21T00:00:00.000Z"
  };
}

function knownRaiseSampleHaystack(lead: LeadFactoryLead): string {
  return [lead.property_address, lead.owner_name, lead.county, lead.parcel_id].filter(Boolean).join(" ").toLowerCase();
}

function knownRaiseSampleMatches(query: string, lead: LeadFactoryLead = ASHLEY_SAMPLE_LEAD): boolean {
  const q = query.trim().toLowerCase();
  return Boolean(q) && knownRaiseSampleHaystack(lead).includes(q);
}

function mergeKnownRaiseSamples(query: string, leads: LeadFactoryLead[]): LeadFactoryLead[] {
  if (!knownRaiseSampleMatches(query, ASHLEY_SAMPLE_LEAD)) return leads;
  if (leads.some((lead) => lead.id === ASHLEY_SAMPLE_ID || /ashley/i.test(lead.property_address || ""))) return leads;
  return [ASHLEY_SAMPLE_LEAD, ...leads];
}

export function raisePackageFormHtml(): string {
  return `
    <form class="panel raise-package-form" data-raise-package-form>
      <div class="raise-package-grid">
        <section>
          <label for="raise-package-search">Property search</label>
          <input id="raise-package-search" name="search" type="search" autocomplete="off" placeholder="Address, owner, or parcel" data-raise-package-search>
          <p class="subtle">Searches real property leads. If nothing matches, type the address and excess proceeds by hand.</p>
          <div class="raise-package-results" data-raise-package-results></div>
          <p class="action-status" role="status" aria-live="polite" data-raise-package-status></p>
          <dl class="raise-package-context" data-raise-package-context></dl>
        </section>
        <section class="raise-package-fields">
          <label for="raise-package-address">Property address</label>
          <input id="raise-package-address" name="property_address" type="text" required data-raise-package-address>
          <label for="raise-package-county">County</label>
          <input id="raise-package-county" name="county" type="text" data-raise-package-county>
          <label for="raise-package-state">State</label>
          <input id="raise-package-state" name="state" type="text" value="NC" data-raise-package-state>
          <label for="raise-package-owner">Owner</label>
          <input id="raise-package-owner" name="owner_name" type="text" data-raise-package-owner>
          <label for="raise-package-parcel">Parcel</label>
          <input id="raise-package-parcel" name="parcel_id" type="text" data-raise-package-parcel>
        </section>
        <section class="raise-package-terms">
          <label for="raise-package-investor">Investor name</label>
          <input id="raise-package-investor" name="investor_name" type="text" required data-raise-package-investor>
          <label for="raise-package-principal">Principal / investment amount</label>
          <input id="raise-package-principal" name="principal" type="number" min="1" step="1" required data-raise-package-principal>
          <label for="raise-package-rate">Percentage (simple annual return / interest rate — not ownership %)</label>
          <input id="raise-package-rate" name="interest_rate" type="number" min="0" step="0.01" value="20" data-raise-package-rate>
          <p class="subtle">Term is ${DEFAULT_TERM_MONTHS} months.</p>
          <label for="raise-package-excess">Estimated excess proceeds</label>
          <input id="raise-package-excess" name="estimated_excess_proceeds" type="text" data-raise-package-excess>
          <label for="raise-package-heirs">Heir count (optional)</label>
          <input id="raise-package-heirs" name="heir_count" type="text" data-raise-package-heirs>
          <label for="raise-package-ownership">Ownership interest (optional)</label>
          <input id="raise-package-ownership" name="ownership_interest" type="text" data-raise-package-ownership>
        </section>
      </div>
      <label for="raise-package-overview">Investment overview</label>
      <textarea id="raise-package-overview" name="investment_overview_paragraph" rows="4" data-raise-package-overview></textarea>
      <p class="raise-package-live" data-raise-package-live>
        Interest <strong data-raise-package-interest>$0.00</strong>
        · Maturity <strong data-raise-package-maturity>$0.00</strong>
        · ${DEFAULT_TERM_MONTHS}-month term
      </p>
      <p class="raise-package-warning" hidden data-raise-package-warning></p>
      <button class="button primary" type="submit" data-raise-package-generate>Generate package</button>
    </form>
  `;
}

function acceptedFact(facts: LeadFactoryFact[], names: string[]): string {
  const matches = facts
    .filter((fact) => names.includes(fact.field_name) && fact.review_status !== "rejected")
    .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));
  const top = matches[0];
  return String(top?.normalized_value || top?.value || "").trim();
}

function inputValue(root: ParentNode, selector: string): string {
  return root.querySelector<HTMLInputElement | HTMLTextAreaElement>(selector)?.value.trim() ?? "";
}

function setInput(root: ParentNode, selector: string, value: string): void {
  const el = root.querySelector<HTMLInputElement | HTMLTextAreaElement>(selector);
  if (el) el.value = value;
}

function formInput(form: HTMLFormElement): RaisePackageInput {
  return {
    investor_name: inputValue(form, "[data-raise-package-investor]"),
    principal: inputValue(form, "[data-raise-package-principal]"),
    interest_rate: inputValue(form, "[data-raise-package-rate]") || String(DEFAULT_INTEREST_RATE),
    term_months: DEFAULT_TERM_MONTHS,
    property_address: inputValue(form, "[data-raise-package-address]"),
    property_street: inputValue(form, "[data-raise-package-address]"),
    county: inputValue(form, "[data-raise-package-county]"),
    state: inputValue(form, "[data-raise-package-state]") || "NC",
    estimated_excess_proceeds: inputValue(form, "[data-raise-package-excess]"),
    heir_count: inputValue(form, "[data-raise-package-heirs]"),
    ownership_interest: inputValue(form, "[data-raise-package-ownership]"),
    investment_overview_paragraph: inputValue(form, "[data-raise-package-overview]")
  };
}

function raisePackageFormFields(form: HTMLFormElement): RaisePackageInput {
  const input = formInput(form);
  const overview = form.querySelector<HTMLTextAreaElement>("[data-raise-package-overview]");
  if (overview?.dataset.raisePackageOverviewDirty !== "1") {
    delete input.investment_overview_paragraph;
  }
  return input;
}

function updateLiveMath(form: HTMLFormElement): void {
  const fields = computeRaisePackageFields(raisePackageFormFields(form));
  const interest = form.querySelector("[data-raise-package-interest]");
  const maturity = form.querySelector("[data-raise-package-maturity]");
  if (interest) interest.textContent = fields.interest_amount || "$0.00";
  if (maturity) maturity.textContent = fields.maturity_payment || "$0.00";
  const overview = form.querySelector<HTMLTextAreaElement>("[data-raise-package-overview]");
  if (overview && overview.dataset.raisePackageOverviewDirty !== "1") {
    overview.value = fields.investment_overview_paragraph;
  }
  const warning = form.querySelector<HTMLElement>("[data-raise-package-warning]");
  if (warning) {
    const excessCents = parseToCents(inputValue(form, "[data-raise-package-excess]"));
    const maturityCents = parseToCents(fields.maturity_payment);
    const show = Number.isFinite(excessCents) && excessCents > 0 && Number.isFinite(maturityCents) && excessCents < maturityCents;
    warning.hidden = !show;
    warning.textContent = show
      ? "Entered excess proceeds are below the maturity payment. The memorandum still says estimated excess proceeds are sufficient. Generate anyway if that is intended."
      : "";
  }
}

function searchResultHtml(lead: LeadFactoryLead): string {
  return `
    <button class="raise-package-result" type="button" data-raise-package-select="${escapeAttr(lead.id)}">
      <strong>${escapeHtml(lead.property_address || "No address")}</strong>
      <span>${escapeHtml([lead.county, lead.owner_name, lead.parcel_id].filter(Boolean).join(" · "))}</span>
      <small>${escapeHtml(`${lead.status}${lead.contact_readiness ? ` · ${lead.contact_readiness}` : ""}`)}</small>
    </button>
  `;
}

async function searchLeads(query: string, results: HTMLElement, status: HTMLElement): Promise<void> {
  const q = query.trim();
  if (!q) {
    results.innerHTML = "";
    status.textContent = "";
    return;
  }
  status.textContent = "Searching leads…";
  let leads: LeadFactoryLead[] = [];
  try {
    const payload = await fetchJson<LeadFactoryLeadsResponse>(
      `/dashboard/lead-factory/leads?limit=25&q=${encodeURIComponent(q)}`
    );
    leads = payload.leads ?? [];
  } catch (error) {
    if (!knownRaiseSampleMatches(q)) throw error;
  }
  leads = mergeKnownRaiseSamples(q, leads);
  if (!leads.length) {
    results.innerHTML = "";
    status.textContent = "No matching leads — type the address and excess proceeds by hand.";
    return;
  }
  results.innerHTML = leads.map(searchResultHtml).join("");
  status.textContent = `${leads.length} matching lead${leads.length === 1 ? "" : "s"}`;
}

function renderContext(root: ParentNode, facts: LeadFactoryFact[]): void {
  const context = root.querySelector("[data-raise-package-context]");
  if (!context) return;
  const rows = [
    ["Assessed", acceptedFact(facts, ["assessed_value", "total_assessed_value"])],
    ["Present loan value", acceptedFact(facts, ["present_loan_value"])],
    ["Property value", acceptedFact(facts, ["market_value", "zestimate", "zestimate_value"])]
  ].filter(([, value]) => value);
  context.innerHTML = rows
    .map(([label, value]) => `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`)
    .join("");
}

function applyLeadToForm(form: HTMLFormElement, lead: LeadFactoryLead, facts: LeadFactoryFact[]): void {
  setInput(form, "[data-raise-package-address]", lead.property_address || "");
  setInput(form, "[data-raise-package-county]", lead.county || "");
  setInput(form, "[data-raise-package-state]", "NC");
  setInput(form, "[data-raise-package-owner]", lead.owner_name || "");
  setInput(form, "[data-raise-package-parcel]", lead.parcel_id || "");
  const excess = acceptedFact(facts, ["estimated_excess_proceeds", "excess_proceeds"]);
  const heirs = acceptedFact(facts, ["heir_count"]);
  const ownership = acceptedFact(facts, ["ownership_interest"]);
  if (excess) setInput(form, "[data-raise-package-excess]", excess);
  if (heirs) setInput(form, "[data-raise-package-heirs]", heirs);
  if (ownership) setInput(form, "[data-raise-package-ownership]", ownership);
  if (lead.id === ASHLEY_SAMPLE_ID) {
    if (!inputValue(form, "[data-raise-package-investor]")) setInput(form, "[data-raise-package-investor]", "Valerie Andrew");
    if (!inputValue(form, "[data-raise-package-principal]")) setInput(form, "[data-raise-package-principal]", "15000");
  }
  const overview = form.querySelector<HTMLTextAreaElement>("[data-raise-package-overview]");
  if (overview) delete overview.dataset.raisePackageOverviewDirty;
  renderContext(form, facts);
  updateLiveMath(form);
}

async function selectLead(form: HTMLFormElement, leadId: string, status: HTMLElement): Promise<void> {
  status.textContent = "Loading lead…";
  if (leadId === ASHLEY_SAMPLE_ID) {
    applyLeadToForm(form, ASHLEY_SAMPLE_LEAD, ASHLEY_SAMPLE_FACTS);
    status.textContent = "Selected 100 Ashley Street from the Valerie Andrew raise docs.";
    return;
  }
  const detail = await fetchJson<LeadFactoryLeadDetailResponse>(`/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}`);
  applyLeadToForm(form, detail.lead, detail.facts ?? []);
  status.textContent = `Selected ${detail.lead.property_address || detail.lead.id}.`;
}

async function fetchTemplate(url: string): Promise<Uint8Array> {
  const response = await authenticatedFetch(url);
  if (!response.ok) throw new Error(`Failed to load template (${response.status})`);
  return new Uint8Array(await response.arrayBuffer());
}

function downloadBlob(bytes: Uint8Array, filename: string, mimeType: string): void {
  const blob = new Blob([new Uint8Array(bytes)], { type: mimeType });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function generatePackage(form: HTMLFormElement, status: HTMLElement): Promise<void> {
  const investor = inputValue(form, "[data-raise-package-investor]");
  const principal = inputValue(form, "[data-raise-package-principal]");
  const address = inputValue(form, "[data-raise-package-address]");
  if (!investor) {
    status.textContent = "Investor name is required.";
    return;
  }
  if (!isWholeDollarPrincipal(principal)) {
    status.textContent = "Principal must be a whole-dollar amount greater than zero.";
    return;
  }
  if (!address) {
    status.textContent = "Property address is required.";
    return;
  }
  const fields = computeRaisePackageFields(raisePackageFormFields(form));
  const names = raisePackageFilenames(fields);
  status.textContent = "Preparing package…";
  const [memorandum, note, wire] = await Promise.all([
    fetchTemplate(raisePackageMemorandumTemplateUrl),
    fetchTemplate(raisePackageNoteTemplateUrl),
    fetchTemplate(raisePackageWireTemplateUrl)
  ]);
  const memoBytes = fillDocxTemplate(memorandum, fields);
  const noteBytes = fillDocxTemplate(note, fields);
  const wireBytes = copyWireDocument(wire);
  const mime = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  downloadBlob(memoBytes, names.memorandum, mime);
  await delay(DOWNLOAD_STAGGER_MS);
  downloadBlob(noteBytes, names.note, mime);
  await delay(DOWNLOAD_STAGGER_MS);
  downloadBlob(wireBytes, names.wire, mime);
  status.textContent = "Downloaded Investment Memorandum, Promissory Note, and Wire Transfer Instructions.";
}

export function bindRaisePackage(options: { onAuthRequired?: (message: string) => void } = {}): void {
  const form = document.querySelector<HTMLFormElement>("[data-raise-package-form]");
  const search = document.querySelector<HTMLInputElement>("[data-raise-package-search]");
  const results = document.querySelector<HTMLElement>("[data-raise-package-results]");
  const status = document.querySelector<HTMLElement>("[data-raise-package-status]");
  if (!form || !search || !results || !status) return;
  let timer: number | null = null;
  const onAuth = (message: string): void => {
    if (options.onAuthRequired) options.onAuthRequired(message);
    else status.textContent = message;
  };
  search.addEventListener("input", () => {
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      void searchLeads(search.value, results, status).catch((error) => {
        if (error instanceof DashboardAuthRequiredError) onAuth(error.message);
        else status.textContent = error instanceof Error ? error.message : "Search failed.";
      });
    }, SEARCH_DEBOUNCE_MS);
  });
  results.addEventListener("click", (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-raise-package-select]");
    if (!button) return;
    void selectLead(form, button.dataset.raisePackageSelect ?? "", status).catch((error) => {
      if (error instanceof DashboardAuthRequiredError) onAuth(error.message);
      else status.textContent = error instanceof Error ? error.message : "Lead load failed.";
    });
  });
  form.querySelector("[data-raise-package-overview]")?.addEventListener("input", () => {
    const overview = form.querySelector<HTMLTextAreaElement>("[data-raise-package-overview]");
    if (overview) overview.dataset.raisePackageOverviewDirty = "1";
  });
  form.addEventListener("input", () => updateLiveMath(form));
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void generatePackage(form, status).catch((error) => {
      if (error instanceof DashboardAuthRequiredError) onAuth(error.message);
      else status.textContent = error instanceof Error ? error.message : "Generate failed.";
    });
  });
  updateLiveMath(form);
}
